import axios from "../utils/axios";

/**
 * List all home sections
 * @param {Object} params - Query parameters
 * @returns {Promise} Home sections list
 */
export const getHomeSections = async (params) => {
    try {
        const { data } = await axios.get('/admin/home-sections', { params });
        return data;
    } catch (error) {
        console.error("🚀 ~ getHomeSections ~ error:", error);
        throw error;
    }
};

/**
 * Get a single home section by ID
 * @param {number|string} id - Home Section ID
 * @returns {Promise} Home section data
 */
export const getHomeSectionById = async (id) => {
    try {
        const { data } = await axios.get(`/admin/home-sections/${id}`);
        return data;
    } catch (error) {
        console.error("🚀 ~ getHomeSectionById ~ error:", error);
        throw error;
    }
};

/**
 * Create a new home section
 * @param {Object} data - Home section data
 * @returns {Promise} Created home section
 */
export const createHomeSection = async (sectionData) => {
    try {
        const { data } = await axios.post('/admin/home-sections', sectionData);
        return data;
    } catch (error) {
        console.error("🚀 ~ createHomeSection ~ error:", error);
        throw error;
    }
};

/**
 * Update an existing home section
 * @param {number|string} id - Home Section ID
 * @param {Object} data - Updated data
 * @returns {Promise} Updated home section
 */
export const updateHomeSection = async (id, sectionData) => {
    try {
        const { data } = await axios.put(`/admin/home-sections/${id}`, sectionData);
        return data;
    } catch (error) {
        console.error("🚀 ~ updateHomeSection ~ error:", error);
        throw error;
    }
};

/**
 * Delete a home section
 * @param {number|string} id - Home Section ID
 * @returns {Promise} Result of deletion
 */
export const deleteHomeSection = async (id) => {
    try {
        const { data } = await axios.delete(`/admin/home-sections/${id}`);
        return data;
    } catch (error) {
        console.error("🚀 ~ deleteHomeSection ~ error:", error);
        throw error;
    }
};

const appendIfDefined = (formData, key, value) => {
    if (value === undefined || value === null || value === '') return;
    formData.append(key, value);
};

const buildHomeSectionItemsFormData = (items) => {
    const formData = new FormData();

    (items || []).forEach((item, index) => {
        const base = `items[${index}]`;

        // Add id field if exists (for updating existing items)
        if (item.id) {
            appendIfDefined(formData, `${base}[id]`, item.id);
        }

        appendIfDefined(formData, `${base}[item_type]`, item.item_type);
        appendIfDefined(formData, `${base}[item_id]`, item.item_id);
        appendIfDefined(formData, `${base}[link_url]`, item.link_url);

        appendIfDefined(formData, `${base}[sort_order]`, item.sort_order ?? index);

        appendIfDefined(formData, `${base}[title][en]`, item?.title?.en);
        appendIfDefined(formData, `${base}[title][ar]`, item?.title?.ar);
        appendIfDefined(formData, `${base}[subtitle][en]`, item?.subtitle?.en);
        appendIfDefined(formData, `${base}[subtitle][ar]`, item?.subtitle?.ar);

        if (item.image instanceof File) {
            appendIfDefined(formData, `${base}[image]`, item.image);
        } else if (typeof item.image === 'string') {
            appendIfDefined(formData, `${base}[image]`, item.image);
        } else if (item.image && typeof item.image === 'object' && typeof item.image.file_name === 'string') {
            appendIfDefined(formData, `${base}[image]`, item.image.file_name);
        }

        if (item.mobile_image instanceof File) {
            appendIfDefined(formData, `${base}[mobile_image]`, item.mobile_image);
        } else if (typeof item.mobile_image === 'string') {
            appendIfDefined(formData, `${base}[mobile_image]`, item.mobile_image);
        } else if (item.mobile_image && typeof item.mobile_image === 'object' && typeof item.mobile_image.file_name === 'string') {
            appendIfDefined(formData, `${base}[mobile_image]`, item.mobile_image.file_name);
        }
    });

    return formData;
};

/**
 * Sync items for a home section (e.g., products for a slider)
 * @param {number|string} id - Home Section ID
 * @param {Array} items - Array of items to sync
 */
export const syncHomeSectionItems = async (id, items) => {
    try {
        const payload = buildHomeSectionItemsFormData(items);
        const { data } = await axios.post(`/admin/home-sections/${id}/items/sync`, payload, {
            headers: { "Content-Type": "multipart/form-data" },
        });
        return data;
    } catch (error) {
        console.error("🚀 ~ syncHomeSectionItems ~ error:", error);
        throw error;
    }
};
